import { CHANNELS } from "../shared/channels";
import { chiCaption } from "../shared/chi_text";
import { CharacterSheet, ReferenceEntry } from "../shared/types";
import { call, listen, mediaSrc } from "./bridge";
import { loadRefs, refsNow } from "./refs_store";
import { byId, cloneTemplate, fillFields } from "./dom";
import { setDots } from "./tweens";
import { setMood } from "./avatar";
import { extractOcIds, extractRefIds, formatChatText } from "./utils";
import { openReference } from "./views/references_view";
import { openOc } from "./views/ocs_view";
import { t } from "./i18n";

// shared ask box so today and the chat tab talk to the same conversation

interface AssistantChunk {
  kind: "text" | "status";
  text: string;
}

let answerElement: HTMLElement | null = null;
let answerText = "";
let waiting = false;
let ocs: CharacterSheet[] = [];

async function loadOcs(): Promise<void> {
  ocs = await call<CharacterSheet[]>(CHANNELS.ocsList);
}

function knownIds(): Set<string> {
  return new Set(refsNow().map((entry) => entry.id));
}

function scrollChat(): void {
  const view = byId("view-chat");
  view.scrollTop = view.scrollHeight;
}

function cardBlurb(entry: ReferenceEntry): string {
  const caption = chiCaption(entry);
  if (caption) return caption;
  return String(entry.note || "").trim();
}

function ocCard(sheet: CharacterSheet): HTMLElement {
  const fetishes = sheet.fetishes.split("\n").map((line) => line.replace(/^-\s*/, "").trim()).filter(Boolean);
  const card = fillFields(cloneTemplate("template-chat-ref"), {
    author: sheet.name,
    seen: sheet.look || sheet.features.split("\n")[0] || "",
    tags: fetishes.slice(0, 10).join(", ")
  });
  const image = card.querySelector("img");
  if (image) image.src = mediaSrc(sheet.imageUrl);
  card.addEventListener("click", () => void openOc(sheet.id));
  return card;
}

function paintRefCards(message: HTMLElement, text: string): void {
  const tray = message.querySelector(".chat-refs");
  if (!tray) return;
  const ids = extractRefIds(text, knownIds());
  const cards = ids.map((id) => refsNow().find((entry) => entry.id === id)).filter((entry): entry is ReferenceEntry => Boolean(entry));
  // only pictured ocs get a card, chi is told to skip the rest anyway
  const sheets = extractOcIds(text).map((id) => ocs.find((sheet) => sheet.id === id)).filter((sheet): sheet is CharacterSheet => Boolean(sheet?.imageUrl));
  tray.replaceChildren(...sheets.map(ocCard), ...cards.map((entry) => {
    const card = fillFields(cloneTemplate("template-chat-ref"), {
      author: entry.author || t("unlabeled"),
      seen: cardBlurb(entry),
      tags: (entry.tags || []).slice(0, 10).join(", ")
    });
    const image = card.querySelector("img");
    if (image) image.src = mediaSrc(entry.thumbUrl || entry.fileUrl);
    card.addEventListener("click", () => void openReference(entry.id));
    return card;
  }));
}

function addMessage(role: "user" | "assistant" | "error", text: string): HTMLElement {
  const message = fillFields(cloneTemplate("template-message"), { text });
  message.classList.add(role);
  byId("chat-messages").append(message);
  scrollChat();
  return message;
}

function setWaiting(isWaiting: boolean, status = ""): void {
  waiting = isWaiting;
  document.querySelectorAll<HTMLButtonElement>("[data-chat-send]").forEach((button) => {
    button.disabled = isWaiting;
  });
  document.querySelectorAll<HTMLButtonElement>("#chat-suggestions .chip").forEach((chip) => {
    chip.disabled = isWaiting;
  });
  const statusEl = document.getElementById("chat-status");
  if (statusEl) {
    statusEl.textContent = status;
    setDots(statusEl, isWaiting);
  }
  const todayReply = document.getElementById("today-chat-status");
  if (todayReply) todayReply.textContent = status;
  if (isWaiting) setMood("thinking");
}

export async function sendChat(text: string): Promise<void> {
  if (waiting || !text.trim()) return;
  addMessage("user", text.trim());
  answerElement = null;
  answerText = "";
  setWaiting(true, t("chix is processing"));
  void loadRefs().catch(() => undefined);
  void loadOcs().catch(() => undefined);
  try {
    await call(CHANNELS.assistantSend, text.trim());
  } catch (error) {
    // the done event normally carries the error, this covers a dropped connection
    if (waiting) void onDone({ error: error instanceof Error ? error.message : String(error) });
  }
}

function onChunk(chunk: AssistantChunk): void {
  if (chunk.kind === "status") {
    setWaiting(true, chunk.text);
    return;
  }
  answerElement ??= addMessage("assistant", "");
  answerText += chunk.text;
  answerElement.querySelector(".bubble-text")!.innerHTML = formatChatText(answerText);
  paintRefCards(answerElement, answerText);
  scrollChat();
  const preview = document.getElementById("today-chat-reply");
  if (preview) preview.innerHTML = formatChatText(answerText);
}

async function onDone(result: { error: string }): Promise<void> {
  if (!waiting) return;
  if (result.error) addMessage("error", result.error);
  if (answerElement && answerText) {
    await Promise.all([loadRefs(), loadOcs()].map((job) => job.catch(() => undefined)));
    paintRefCards(answerElement, answerText);
  }
  setWaiting(false);
  setMood(result.error ? "idle" : "happy");
}

export function wireAskForm(formId: string, inputId: string): void {
  const form = byId<HTMLFormElement>(formId);
  const input = byId<HTMLTextAreaElement>(inputId);
  const fit = () => {
    input.style.height = "auto";
    input.style.height = `${Math.min(input.scrollHeight + 2, 140)}px`;
  };
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    void sendChat(input.value);
    input.value = "";
    fit();
  });
  input.addEventListener("input", fit);
  input.addEventListener("keydown", (event) => {
    // on touch keyboards enter makes a new line, the send key sends
    if (event.key !== "Enter" || event.shiftKey || matchMedia("(hover: none)").matches) return;
    event.preventDefault();
    form.requestSubmit();
  });
}

export function setupChatBox(): void {
  wireAskForm("chat-form", "chat-input");
  byId("chat-suggest").addEventListener("click", () => void sendChat(t("What should I work on next?")));
  document.querySelectorAll<HTMLButtonElement>("#chat-suggestions [data-ask]").forEach((chip) => {
    chip.addEventListener("click", () => void sendChat(chip.dataset.ask || ""));
  });
  byId("chat-reset").addEventListener("click", async () => {
    if (waiting) return;
    await call(CHANNELS.assistantReset);
    byId("chat-messages").replaceChildren(cloneTemplate("template-greeting"));
    const preview = document.getElementById("today-chat-reply");
    if (preview) preview.textContent = "";
  });
  byId("chat-memory").addEventListener("click", () => call(CHANNELS.assistantOpenMemory));
  byId("chat-messages").replaceChildren(cloneTemplate("template-greeting"));
  listen<AssistantChunk>(CHANNELS.assistantChunk, onChunk);
  listen<{ error: string }>(CHANNELS.assistantDone, (result) => void onDone(result));
}
